import { ServiceTypeEnum } from '@sr-sdks/api-sdk-axios';
import {
  Card,
  CardContent,
  Icon,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@sr/ui-library';
import { useState } from 'react';
import { SERVICE_BADGE_PRESETS } from '../../../../components/Badges/appBadgeConstants';
import { MSR3Empty } from '../../../../components/MSR3Empty';
import { ErrorFallback } from '../../../../components/QueryResults/ErrorFallback';
import { PAYMENTS_SEARCH_PLACEHOLDERS } from '../../../../constant/paymentsSearchPlaceholders';
import { useActiveWorkspace } from '../../../../hooks/useActiveWorkspace';
import { StatementsDataTable } from '../../Title/Summary/StatementsDataTable';
import { useClientStatementTabs } from './useClientStatementTabs';

interface ClientStatementsSectionProps {
  clientId: string;
}

/**
 * MSR3 Clients Summary "Statements" card. Service-type tabs filter a single
 * paginated, sortable, searchable statements table scoped to `clientId`.
 *
 * Deliberately mirrors `TitleStatementsSection`'s actual chrome — a static
 * `<h2>` heading plus a `Card`/`CardContent` wrapper containing the tabs and
 * table (search bar included) — rather than the `EntityList` wrapper the
 * other Client Summary sections use. `EntityList` has no card background, so
 * reusing it here would leave the search bar sitting outside any contained
 * surface, unlike every other statements/table view in the app.
 *
 * As on Title Summary, a tab shows for each service the client is registered
 * for, whether or not it has been paid yet: a registered tab with nothing
 * listed opens on the table's own empty state. `Client` has no `serviceType`
 * field, so `useClientStatementTabs` reads registration from the titles the
 * client owns in this workspace (see there for how complete that is for
 * Disbursements and Residuals). A tab also shows when the client has at
 * least one finalised statement (Royalties) or paid invoice
 * (Disbursements/CAM, Residuals) under it, so an incomplete registration
 * record never hides real records. The view defaults to the first enabled tab
 * with listed records, so it opens on rows whenever there are any, else the
 * first enabled tab; as on Title Summary, the tab bar is hidden when only one
 * tab applies. Unlike Title Summary, a client with no tabs keeps the card,
 * with an empty state in it, so the card never appears and then vanishes
 * while the checks run. The heading renders immediately; a skeleton stands in
 * for the tab bar and table until every check has come back (no premature
 * "all tabs" flash). A failed check defers to its tab's other check; an error
 * replaces both once every records check has failed, since no tab's table
 * could load.
 *
 * `StatementsDataTable` picks its API from `serviceTypes` (Statements API for
 * royalties, Invoices API for the rest) and threads `beneficiaryClientId` into
 * *both*, so every tab stays scoped to this one client. The Client column is
 * hidden throughout — the page is already scoped to this client (ADR-CLP-002) —
 * so the search placeholder drops "client" too, and "reference ID" with it,
 * since this table has no Reference ID column to search on.
 *
 * This component only owns the tab/filter state and the card chrome; the table
 * itself is the shared `StatementsDataTable`.
 */
export const ClientStatementsSection = ({
  clientId,
}: ClientStatementsSectionProps) => {
  const [activeWorkspace] = useActiveWorkspace();
  const { defaultTab, enabledTabs, error } = useClientStatementTabs(
    clientId,
    activeWorkspace?._id,
  );

  const [manualService, setManualService] = useState<ServiceTypeEnum | null>(
    null,
  );

  // Falls back to the hook's default (the first enabled tab with listed
  // records, else the first enabled tab) whenever there's no manual selection,
  // or the manually-selected one isn't enabled for this client.
  const activeTab =
    enabledTabs?.find(({ service }) => service === manualService) ?? defaultTab;

  // Show the tab bar only when there are multiple enabled tabs to switch between.
  const hasMultipleTabs = (enabledTabs?.length ?? 0) > 1;

  const renderContent = () => {
    if (error) return <ErrorFallback error={error} />;

    if (!enabledTabs) {
      return (
        <>
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-64 w-full" />
        </>
      );
    }

    // Every check has come back and this client is registered for no tab's
    // services and has no listed records under any tab.
    if (!activeTab) return <MSR3Empty {...MSR3Empty.Props.payment} />;

    return (
      <>
        {hasMultipleTabs && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Tabs
              value={activeTab.service}
              onValueChange={(value) =>
                setManualService(value as ServiceTypeEnum)
              }
            >
              <TabsList>
                {enabledTabs.map(({ service }) => (
                  <TabsTrigger
                    key={service}
                    className="gap-1.5"
                    value={service}
                  >
                    <Icon
                      {...SERVICE_BADGE_PRESETS[service].iconProps}
                      size="sm"
                    />
                    {SERVICE_BADGE_PRESETS[service].children}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        )}

        <StatementsDataTable
          clientId={clientId}
          hideClientColumn
          paginationKey="clientStatementsPage"
          searchPlaceholder={
            PAYMENTS_SEARCH_PLACEHOLDERS.clientSummaryStatements
          }
          serviceTypes={activeTab.serviceTypes}
          showTopPagination
          workspaceId={activeWorkspace?._id}
        />
      </>
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-base font-medium text-foreground">Statements</h2>

      <Card>
        <CardContent className="flex flex-col gap-4 p-6">
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
};
